//Built-in libraries

//Third party libraries
import {
  BanknotesIcon,
  ChartBarIcon,
  ClipboardDocumentCheckIcon,
  ClipboardDocumentListIcon,
  DocumentTextIcon,
  IdentificationIcon,
} from '@heroicons/react/24/outline';
import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { postApplication } from '@/api/Rover/ApplicationForm';
import SubmitSuccess from '@/components/Applications/SubmitSuccess';
import Button from '@/components/UI/Button';
import { HookForm } from '@/components/UI/HookForm/Form';
//Custom components
import { HookFormProvider } from '@/components/UI/HookForm/Provider';
//Services
import { ApplicationFormProvider, useApplicationFormContext } from '@/features/ApplicationForm/ApplicationFormProvider';

//STP Libraries

import type { IGetApplicationFormInfoServiceRes, IPostApplicationServicePayload } from '@/api/Rover/ApplicationForm/types';
import DefaultApplicantInformation from '../../Components/DefaultApplicantInformation';
import Capacity from './Capacity';
//Assets and Miscellaneous
import { initialValues } from './constants';
import Information from './Information';
import LegalStatus from './LegalStatus';
import OrganizationStructure from './OrganizationalStructure';
import OrganizationDocuments from './OrganizationDocuments';
import Review from './Review';
import { validationSchema } from './validationSchema';

//Component Begin

const steps = [
  {
    name: 'Applicant Information',
    sectionId: 'applicantInformation',
    Icon: IdentificationIcon,
    component: <DefaultApplicantInformation />,
  },
  {
    name: 'Information',
    sectionId: 'step1',
    Icon: ClipboardDocumentListIcon,
    component: <Information />,
  },
  {
    name: 'Organization Documents',
    sectionId: 'step2',
    Icon: ChartBarIcon,
    component: <OrganizationDocuments />,
  },
  {
    name: 'Legal Status',
    sectionId: 'step3',
    Icon: DocumentTextIcon,
    component: <LegalStatus />,
  },
  {
    name: 'Organization Structure',
    sectionId: 'step4',
    Icon: BanknotesIcon,
    component: <OrganizationStructure />,
  },
  {
    name: 'Capacity',
    sectionId: 'step5',
    Icon: DocumentTextIcon,
    component: <Capacity />,
  },
  {
    name: 'Review',
    sectionId: 'review',
    Icon: ClipboardDocumentCheckIcon,
    component: <Review />,
  },
];

interface ICHDOApplicationFormProps {
  formData: IGetApplicationFormInfoServiceRes;
}

const ApplicationFormBase = ({ formData }: ICHDOApplicationFormProps) => {
  const [isSubmitSuccess, setIsSubmitSuccess] = useState(false);
  const { currentStep, setCurrentStep, updateData, data } = useApplicationFormContext();
  const isLast = currentStep === steps.length - 1;

  const submitApplication = useMutation((data: IPostApplicationServicePayload) => postApplication({ data }), {
    onSuccess: () => {
      setIsSubmitSuccess(true);
    },
  });

  const onSubmitHandler = (values: Record<string, any>) => {
    if (!isLast) {
      updateData({
        [steps[currentStep].sectionId]: values[steps[currentStep].sectionId],
      });
      setCurrentStep((prev) => prev + 1);
    } else {
      // Collect all files from different steps
      const allFiles = [
        // Step 2 - Organization Documents
        ...(data?.step2?.f1?.selectedFiles || []),
        ...(data?.step2?.f2?.selectedFiles || []),
        ...(data?.step2?.f3?.selectedFiles || []),
        ...(data?.step2?.f4?.selectedFiles || []),
        ...(data?.step2?.f5?.selectedFiles || []),
        // Step 3 - Legal Status
        ...(data?.step3?.f1?.selectedFiles || []),
        ...(data?.step3?.f2?.selectedFiles || []),
        // Step 4 - Organization Structure
        ...(data?.step4?.f1?.selectedFiles || []),
        ...(data?.step4?.f2?.selectedFiles || []),
        ...(data?.step4?.f3?.selectedFiles || []),
        // Step 5 - Capacity
        ...(data?.step5?.f1?.selectedFiles || []),
        ...(data?.step5?.f2?.selectedFiles || []),
        ...(data?.step5?.f3?.selectedFiles || []),
        ...(data?.step5?.f4?.selectedFiles || []),
        ...(data?.step5?.f5?.selectedFiles || []),
        ...(data?.step5?.f6?.selectedFiles || []),
      ].filter((file) => file?.key && file.path); // Filter out any invalid files

      const applicationData: IPostApplicationServicePayload = {
        formId: formData?.data?.id,
        contactName: data.applicantInformation.fullName,
        contactEmail: data.applicantInformation.contactEmail,
        applicationData: { ...data },
        files: allFiles,
      };
      submitApplication.mutate(applicationData);
    }
  };

  if (isSubmitSuccess) {
    return <SubmitSuccess message={formData.data.confirmationMessage ?? 'Thank you for your interest in CHDO.'} />;
  }

  return (
    <div className='container mx-auto'>
      <h2>{formData?.data?.name}</h2>
      <div className='flex mb-10'>
        <aside className='w-1/4 py-6 px-2 sm:px-6 bg-white rounded-l-md'>
          <div className='w-full flex justify-center'>
            <img src={formData?.data.logo} alt='Form Logo' className='h-14 xl:h-20 object-scale-down ' />
          </div>
          <nav className='space-y-1 py-5'>
            {steps.map(({ name, Icon }, index) => (
              <button
                key={index}
                className={`${
                  currentStep === index ? 'bg-neutralColor-lightest text-primaryColor-normal' : 'text-neutralColor-normal'
                } group rounded-md px-3 py-2 flex items-center text-sm font-medium w-full`}
              >
                {/* <Icon className='shrink-0 -ml-1 mr-3 h-6 w-6' /> */}
                {name}
              </button>
            ))}
          </nav>
        </aside>
        <div className='w-full bg-gray-50'>
          <HookFormProvider
            defaultValues={initialValues}
            mode='all'
            resolver={validationSchema?.[currentStep] && yupResolver(validationSchema[currentStep] as any)}
          >
            <HookForm onSubmit={onSubmitHandler}>
              {steps[currentStep].component}
              {isLast && (
                <div className='flex gap-4 justify-end p-6'>
                  <Button
                    label='Previous'
                    btnStyle='neutral'
                    type='button'
                    disabled={submitApplication.isLoading}
                    onClick={() => setCurrentStep((prev) => prev - 1)}
                  />
                  <Button
                    label={`${submitApplication.isLoading ? 'Submitting...' : 'Submit'}`}
                    btnStyle='primary'
                    type='submit'
                    disabled={submitApplication.isLoading}
                  />
                </div>
              )}
            </HookForm>
          </HookFormProvider>
        </div>
      </div>
    </div>
  );
};

export const CHDOApplicationForm = ({ ...props }: ICHDOApplicationFormProps) => {
  return (
    <ApplicationFormProvider initialValues={initialValues}>
      <ApplicationFormBase {...props} />
    </ApplicationFormProvider>
  );
};
